import { useEffect, useState } from 'react';
import Btn from './ui/Btn';
import InvitationsPanel from './InvitationsPanel';
import { ApiError, api } from '../lib/api';
import { toast } from '../stores/toaster';
import type { Collaborator } from '../lib/types';

type TenantUser = {
  id: string;
  email: string;
  display_name: string;
  role: string;
};

type Props = {
  dossierId: string;
  accessMode: 'all' | 'restricted';
  onClose: () => void;
  onChange: () => void;
};

/**
 * Panneau « Accès au dossier ».
 *
 * - Toggle access_mode (all / restricted) → PATCH /dossiers/:id.
 * - Liste des collaborateurs explicites + ajout/suppression.
 * - Les admins voient toujours tout (rappelé dans la note de bas).
 */
export default function DossierAccessPanel({ dossierId, accessMode, onClose, onChange }: Props) {
  const [mode, setMode] = useState<'all' | 'restricted'>(accessMode);
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState<string>('');

  useEffect(() => {
    void api
      .get<Collaborator[]>(`/dossiers/${dossierId}/collaborators`)
      .then(setCollaborators)
      .catch(() => setCollaborators([]));
    void api
      .get<TenantUser[]>('/auth/users')
      .then(setUsers)
      .catch(() => setUsers([]));
  }, [dossierId]);

  const persistMode = async (next: 'all' | 'restricted') => {
    setBusy(true);
    try {
      await api.patch(`/dossiers/${dossierId}`, { access_mode: next });
      setMode(next);
      toast.success(next === 'all' ? 'Accès ouvert au cabinet' : 'Accès restreint à la sélection');
      onChange();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setBusy(false);
    }
  };

  const addCollaborator = async () => {
    if (!adding) return;
    setBusy(true);
    try {
      await api.post(`/dossiers/${dossierId}/collaborators`, {
        user_id: adding,
        permission: 'editor',
      });
      const fresh = await api.get<Collaborator[]>(`/dossiers/${dossierId}/collaborators`);
      setCollaborators(fresh);
      setAdding('');
      toast.success('Collaborateur ajouté');
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setBusy(false);
    }
  };

  const removeCollaborator = async (userId: string) => {
    setBusy(true);
    try {
      await api.delete(`/dossiers/${dossierId}/collaborators/${userId}`);
      setCollaborators((prev) => prev.filter((c) => c.user_id !== userId));
      toast.success('Collaborateur retiré');
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setBusy(false);
    }
  };

  const collabIds = new Set(collaborators.map((c) => c.user_id));
  const addable = users.filter((u) => !collabIds.has(u.id));

  return (
    <div className="rounded-lg border border-rule bg-paper-card p-5">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-serif text-[16px] font-medium text-text-primary">
          👥 Accès au dossier
        </h2>
        <Btn variant="ghost" size="sm" onClick={onClose}>
          Fermer
        </Btn>
      </div>

      <fieldset className="space-y-1.5">
        <legend className="text-[10px] font-medium uppercase tracking-[1px] text-text-secondary">
          Périmètre
        </legend>
        <div className="flex flex-wrap gap-3 text-[13px]">
          <label className="flex items-center gap-1.5">
            <input
              type="radio"
              name="dossier-access-mode"
              value="all"
              checked={mode === 'all'}
              disabled={busy}
              onChange={() => void persistMode('all')}
            />
            Tout le cabinet
          </label>
          <label className="flex items-center gap-1.5">
            <input
              type="radio"
              name="dossier-access-mode"
              value="restricted"
              checked={mode === 'restricted'}
              disabled={busy}
              onChange={() => void persistMode('restricted')}
            />
            Sélection
          </label>
        </div>
        <p className="text-[11px] text-text-tertiary">
          Les administrateurs voient toujours tous les dossiers du cabinet.
        </p>
      </fieldset>

      {mode === 'restricted' && (
        <div className="mt-4 space-y-3">
          <h3 className="text-[10px] font-medium uppercase tracking-[1px] text-text-secondary">
            Collaborateurs ({collaborators.length})
          </h3>
          {collaborators.length === 0 ? (
            <p className="text-[12px] text-text-tertiary">
              Aucun collaborateur. Tu (créateur) gardes l'accès.
            </p>
          ) : (
            <ul className="space-y-1">
              {collaborators.map((c) => (
                <li
                  key={c.user_id}
                  className="flex items-center justify-between gap-2 rounded-sm border border-rule bg-paper-elevated px-3 py-1.5 text-[13px]"
                >
                  <span>
                    <span className="font-medium text-text-primary">{c.display_name}</span>
                    <span className="ml-1.5 font-mono text-[10px] uppercase text-text-tertiary">
                      {c.permission}
                    </span>
                  </span>
                  <Btn
                    variant="ghost"
                    size="sm"
                    onClick={() => void removeCollaborator(c.user_id)}
                    disabled={busy}
                    title="Retirer cet accès"
                  >
                    ✕
                  </Btn>
                </li>
              ))}
            </ul>
          )}

          {addable.length > 0 && (
            <div className="flex items-center gap-2">
              <select
                value={adding}
                onChange={(e) => setAdding(e.target.value)}
                className="flex-1 rounded-md border border-rule-strong bg-paper-elevated px-2 py-1.5 text-[13px]"
                disabled={busy}
              >
                <option value="">— ajouter un utilisateur —</option>
                {addable.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.display_name} · {u.role}
                  </option>
                ))}
              </select>
              <Btn
                variant="primary"
                size="sm"
                onClick={() => void addCollaborator()}
                disabled={busy || !adding}
              >
                Ajouter
              </Btn>
            </div>
          )}
        </div>
      )}

      {/* Sprint B / ADR-019 — invitations externes */}
      <div className="mt-6 border-t border-rule pt-4">
        <InvitationsPanel dossierId={dossierId} />
      </div>
    </div>
  );
}
